import { useEffect, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { Controller, type FieldPath, type FieldValues } from "react-hook-form";
import { getThemeClass, getThemeComponentGroup } from "@/src_new/shared/lib/themeComponentClasses";

import { inputClassName } from "./TextInput";
import type { BaseInputProps, InputOption } from "@/src_new/shared/types";
import { WrapperInput } from "./WrapperInput";

type SelectInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
> = BaseInputProps<TFieldValues, TName> & {
  options: InputOption<TValue>[];
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearchTermChange?: (value: string) => void;
};

export function SelectInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
>({
  control,
  name,
  label,
  placeholder = "Select option",
  description,
  disabled,
  required,
  className,
  options,
  searchable = false,
  searchPlaceholder = "Search options",
  onSearchTermChange,
}: SelectInputProps<TFieldValues, TName, TValue>) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [openDirection, setOpenDirection] = useState<"down" | "up">("down");
  const [menuMaxHeight, setMenuMaxHeight] = useState(100);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputClasses = getThemeComponentGroup("input");
  const selectClasses = getThemeComponentGroup("select");

  const getScrollableAncestor = (element: HTMLElement | null): HTMLElement | null => {
    let current = element?.parentElement ?? null;

    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      const isClippingBoundary = ["auto", "scroll", "overlay", "hidden", "clip"].includes(
        overflowY
      );

      if (isClippingBoundary) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  };

  const resolveMenuDirection = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const estimatedMenuHeight = 224; // aligns with max-h-56
    const menuGap = 8;
    const minimumMenuHeight = 40;
    const viewportPadding = 16;
    const scrollableAncestor = getScrollableAncestor(containerRef.current);
    const ancestorRect = scrollableAncestor?.getBoundingClientRect();
    const boundaryTop = ancestorRect ? ancestorRect.top + viewportPadding : viewportPadding;
    const boundaryBottom = ancestorRect
      ? ancestorRect.bottom - viewportPadding
      : window.innerHeight - viewportPadding;
    const boundaryHeight = Math.max(minimumMenuHeight, boundaryBottom - boundaryTop);

    const spaceBelow = boundaryBottom - rect.bottom - menuGap;
    const spaceAbove = rect.top - boundaryTop - menuGap;

    const direction: "up" | "down" =
      spaceBelow < minimumMenuHeight && spaceAbove > spaceBelow
        ? "up"
        : spaceAbove < minimumMenuHeight && spaceBelow > spaceAbove
          ? "down"
          : spaceBelow >= spaceAbove
            ? "down"
            : "up";
    const availableSpace = direction === "up" ? spaceAbove : spaceBelow;
    const resolvedMaxHeight = Math.max(
      minimumMenuHeight,
      Math.min(
        estimatedMenuHeight,
        boundaryHeight,
        Math.floor(Math.max(availableSpace, minimumMenuHeight))
      )
    );

    setOpenDirection(direction);
    setMenuMaxHeight(resolvedMaxHeight);
  };

  const openMenu = () => {
    resolveMenuDirection();
    setOpen(true);
  };

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    resolveMenuDirection();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setHighlightedIndex(-1);
      onSearchTermChange?.("");
      return;
    }

    const handleViewportChange = () => resolveMenuDirection();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    if (searchable) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open, searchable, onSearchTermChange]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedOption = options.find((option) => option.value === field.value);
        const normalizedSearch = searchTerm.trim().toLowerCase();
        const filteredOptions = normalizedSearch
          ? options.filter((option) => option.label.toLowerCase().includes(normalizedSearch))
          : options;
        const displayValue = open && searchable ? searchTerm : (selectedOption?.label ?? "");

        const commitSelection = (option: InputOption<TValue>) => {
          field.onChange(option.value);
          setSearchTerm("");
          setHighlightedIndex(-1);
          setOpen(false);
        };

        const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
          if (!searchable) return;

          if (event.key === "ArrowDown") {
            event.preventDefault();
            if (!open) {
              openMenu();
              setHighlightedIndex(filteredOptions.length ? 0 : -1);
              return;
            }

            setHighlightedIndex((current) => {
              if (!filteredOptions.length) return -1;
              return current < filteredOptions.length - 1 ? current + 1 : 0;
            });
            return;
          }

          if (event.key === "ArrowUp") {
            event.preventDefault();
            if (!open) {
              openMenu();
              setHighlightedIndex(filteredOptions.length ? filteredOptions.length - 1 : -1);
              return;
            }

            setHighlightedIndex((current) => {
              if (!filteredOptions.length) return -1;
              return current > 0 ? current - 1 : filteredOptions.length - 1;
            });
            return;
          }

          if (event.key === "Enter") {
            if (!open) return;
            event.preventDefault();

            const optionToSelect =
              highlightedIndex >= 0 ? filteredOptions[highlightedIndex] : filteredOptions[0];

            if (optionToSelect && !optionToSelect.disabled) {
              commitSelection(optionToSelect);
            }
            return;
          }

          if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
            return;
          }
        };

        return (
          <WrapperInput
            label={label}
            error={fieldState.error?.message}
            description={description}
            required={required}
            className={className}
          >
            <div ref={containerRef} className={clsx("relative", open && "z-50")}>
              <div
                className={clsx(
                  inputClassName,
                  "flex items-center justify-between gap-3 text-left",
                  getThemeClass(
                    selectClasses.borderColor || inputClasses.borderColor,
                    "border-theme-border-strong"
                  ),
                  getThemeClass(selectClasses.bgColor || inputClasses.bgColor, "bg-theme-surface"),
                  getThemeClass(
                    selectClasses.textColor || inputClasses.textColor,
                    "text-theme-text"
                  ),
                  getThemeClass(inputClasses.focusBorderColor, "focus:border-theme-primary"),
                  "focus:ring-2",
                  getThemeClass(inputClasses.focusRingColor, "focus:ring-theme-primary/10"),
                  inputClasses.base,
                  selectClasses.trigger,
                  fieldState.error &&
                    clsx(
                      inputClasses.errorBorderColor || "border-theme-danger-bright",
                      "focus:ring-theme-danger-bright/10"
                    )
                )}
              >
                {searchable ? (
                  <input
                    ref={inputRef}
                    type="text"
                    value={displayValue}
                    disabled={disabled}
                    placeholder={open ? searchPlaceholder : placeholder}
                    onFocus={() => openMenu()}
                    onClick={() => openMenu()}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setSearchTerm(nextValue);
                      onSearchTermChange?.(nextValue);
                      setHighlightedIndex(0);
                      if (!open) {
                        openMenu();
                      }
                    }}
                    onKeyDown={handleSearchKeyDown}
                    onBlur={() => {
                      window.setTimeout(() => {
                        setSearchTerm("");
                      }, 100);
                    }}
                    className={clsx(
                      "min-w-0 flex-1 truncate bg-transparent text-[13px] outline-none",
                      selectedOption || searchTerm
                        ? getThemeClass(
                            selectClasses.textColor || inputClasses.textColor,
                            "text-theme-text"
                          )
                        : getThemeClass(
                            selectClasses.placeholderColor || inputClasses.placeholderColor,
                            "text-theme-text-placeholder"
                          ),
                      getThemeClass(
                        selectClasses.placeholderColor || inputClasses.placeholderColor,
                        "placeholder:text-theme-text-placeholder"
                      )
                    )}
                  />
                ) : (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (open) {
                        setOpen(false);
                        return;
                      }
                      openMenu();
                    }}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span
                      className={clsx(
                        "block truncate",
                        selectedOption
                          ? getThemeClass(
                              selectClasses.textColor || inputClasses.textColor,
                              "text-theme-text"
                            )
                          : getThemeClass(
                              selectClasses.placeholderColor || inputClasses.placeholderColor,
                              "text-theme-text-placeholder"
                            )
                      )}
                    >
                      {selectedOption?.label ?? placeholder}
                    </span>
                  </button>
                )}
                <ChevronDown
                  className={clsx(
                    "h-4 w-4 shrink-0 transition",
                    getThemeClass(
                      selectClasses.placeholderColor || inputClasses.placeholderColor,
                      "text-theme-text-soft"
                    ),
                    open && "rotate-180"
                  )}
                  onClick={() => {
                    if (disabled) return;
                    if (open) {
                      setOpen(false);
                    } else {
                      openMenu();
                    }
                    if (!open && searchable) {
                      requestAnimationFrame(() => {
                        inputRef.current?.focus();
                      });
                    }
                  }}
                />
              </div>

              {open ? (
                <div
                  className={clsx(
                    "absolute z-50 w-full overflow-auto thin-scrollbar rounded-lg border shadow-lg",
                    openDirection === "up" ? "bottom-full mb-2" : "top-full mt-2",
                    getThemeClass(
                      selectClasses.borderColor || inputClasses.borderColor,
                      "border-theme-border"
                    ),
                    getThemeClass(
                      selectClasses.bgColor || inputClasses.bgColor,
                      "bg-theme-surface"
                    ),
                    selectClasses.menu
                  )}
                  style={{ maxHeight: `${menuMaxHeight}px` }}
                >
                  {filteredOptions.length ? (
                    filteredOptions.map((option) => (
                      <button
                        key={String(option.value)}
                        type="button"
                        disabled={option.disabled}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          if (option.disabled) return;
                          commitSelection(option);
                        }}
                        onMouseEnter={() =>
                          setHighlightedIndex(
                            filteredOptions.findIndex((entry) => entry.value === option.value)
                          )
                        }
                        className={clsx(
                          "block w-full px-4 py-3 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-50",
                          getThemeClass(
                            selectClasses.textColor || inputClasses.textColor,
                            "text-theme-text"
                          ),
                          getThemeClass(
                            selectClasses.optionHoverBgColor,
                            "hover:bg-theme-surface-soft"
                          ),
                          highlightedIndex >= 0 &&
                            filteredOptions[highlightedIndex]?.value === option.value &&
                            getThemeClass(
                              selectClasses.optionHoverBgColor,
                              "bg-theme-surface-soft"
                            ),
                          selectClasses.option
                        )}
                      >
                        {option.label}
                      </button>
                    ))
                  ) : (
                    <div
                      className={clsx(
                        "px-4 py-3 text-sm",
                        getThemeClass(
                          selectClasses.placeholderColor || inputClasses.placeholderColor,
                          "text-theme-text-soft"
                        )
                      )}
                    >
                      No matching options found
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </WrapperInput>
        );
      }}
    />
  );
}
